import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { type NextBestAction } from '@post-office/confluence-next-best-action';

import { RovoOnboardingBanner } from './components/rovo-onboarding-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../../index';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
} from '@post-office/placement-common-v2';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

const PLACEMENT_LABEL = 'Confluence Rovo onboarding banner';
const MESSAGE_TEMPLATE_ID = 'confluence-rovo-onboarding-banner';

const hydrateStage = async ({
	message,
	request,
}: {
	message: {
		messageInstanceId: string;
		messageTemplateId: string;
		triggerId: string;
		context: unknown;
	};
	request: { logger: { info: (ctx: unknown, msg: string) => void } };
}): Promise<{
	nextBestActionType: NextBestAction;
	tenantId?: string;
	orgId?: string;
}> => {
	request.logger.info(
		{
			messageInstanceId: message.messageInstanceId,
			messageTemplateId: message.messageTemplateId ?? MESSAGE_TEMPLATE_ID,
			placementLabel: PLACEMENT_LABEL,
		},
		'Rovo onboarding banner hydrated successfully',
	);
	return message.context as MessageTemplateContext;
};

/** Exposed for unit testing the hydrate stage. */
export const __hydrateStageForTest: ({
	message,
	request,
}: {
	message: {
		messageInstanceId: string;
		messageTemplateId: string;
		triggerId: string;
		context: unknown;
	};
	request: { logger: { info: (ctx: unknown, msg: string) => void } };
}) => Promise<{
	nextBestActionType: NextBestAction;
	tenantId?: string;
	orgId?: string;
}> = hydrateStage;

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: (input) => {
			const context = input.message.context as MessageTemplateContext;
			return {
				goalMetadata: {
					primaryGoal: {
						goalId: 'clickthrough',
					},
				},
				nextBestAction: context.nextBestActionType,
			};
		},
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: hydrateStage,
	},
}));

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			nextBestActionType,
			orgId,
			handleOnDismissed,
			onSideNavClick,
		}: MessageFrontendProps) => {
			return (
				<RovoOnboardingBanner
					action={nextBestActionType}
					orgId={orgId}
					onPrimaryActionClick={onSideNavClick}
					onDismissClick={handleOnDismissed}
				/>
			);
		},
	},
});
